import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  EnterOutlined,
} from "@ant-design/icons";
import { faqAnswerHasText, sanitizeFaqHtml } from "./sanitizeFaqHtml";

const defaultFormats = {
  bold: false,
  italic: false,
  underline: false,
  ul: false,
  ol: false,
};

const ToolboxButton = ({ title, onClick, active, children }) => (
  <Tooltip title={title}>
    <button
      type="button"
      className={`faq-editor-btn${active ? " is-active" : ""}`}
      aria-pressed={active ? "true" : "false"}
      onMouseDown={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      {children}
    </button>
  </Tooltip>
);

const FaqAnswerEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const skipValueSync = useRef(false);
  const [focused, setFocused] = useState(false);
  const [formats, setFormats] = useState(defaultFormats);
  const isEmpty = !faqAnswerHasText(value);

  const refreshFormats = useCallback(() => {
    if (typeof document.queryCommandState !== "function") {
      return;
    }
    setFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      ul: document.queryCommandState("insertUnorderedList"),
      ol: document.queryCommandState("insertOrderedList"),
    });
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshFormats);
    return () => document.removeEventListener("selectionchange", refreshFormats);
  }, [refreshFormats]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) {
      return;
    }
    if (skipValueSync.current) {
      skipValueSync.current = false;
      return;
    }
    const next = value || "";
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [value]);

  const emitChange = () => {
    skipValueSync.current = true;
    const html = sanitizeFaqHtml(editorRef.current?.innerHTML || "");
    onChange?.(html);
    refreshFormats();
  };

  const run = (command, commandValue) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    if (document.activeElement !== editor) {
      editor.focus();
    }
    document.execCommand("styleWithCSS", false, false);
    if (command === "insertLineBreak") {
      document.execCommand("insertHTML", false, "<br>");
    } else {
      document.execCommand(command, false, commandValue);
    }
    emitChange();
  };

  return (
    <div className={`faq-editor${focused ? " is-focused" : ""}`}>
      <div className="faq-editor-toolbox" role="toolbar" aria-label="Answer formatting">
        <div className="faq-editor-group">
          <ToolboxButton title="Bold" active={formats.bold} onClick={() => run("bold")}>
            <BoldOutlined />
          </ToolboxButton>
          <ToolboxButton title="Italic" active={formats.italic} onClick={() => run("italic")}>
            <ItalicOutlined />
          </ToolboxButton>
          <ToolboxButton title="Underline" active={formats.underline} onClick={() => run("underline")}>
            <UnderlineOutlined />
          </ToolboxButton>
        </div>
        <div className="faq-editor-group">
          <ToolboxButton title="Bullet list" active={formats.ul} onClick={() => run("insertUnorderedList")}>
            <UnorderedListOutlined />
          </ToolboxButton>
          <ToolboxButton title="Numbered list" active={formats.ol} onClick={() => run("insertOrderedList")}>
            <OrderedListOutlined />
          </ToolboxButton>
        </div>
        <div className="faq-editor-group">
          <ToolboxButton title="Line break" onClick={() => run("insertLineBreak")}>
            <EnterOutlined />
          </ToolboxButton>
        </div>
      </div>
      <div
        ref={editorRef}
        className={`faq-editor-surface${isEmpty ? " is-empty" : ""}`}
        contentEditable
        suppressContentEditableWarning
        data-placeholder="Write the answer. Use Enter for a new paragraph."
        onInput={emitChange}
        onKeyUp={refreshFormats}
        onMouseUp={refreshFormats}
        onFocus={() => {
          setFocused(true);
          refreshFormats();
        }}
        onBlur={() => {
          setFocused(false);
          emitChange();
        }}
      />
    </div>
  );
};

export default FaqAnswerEditor;
